import * as React from 'react';
import * as Popover from '@radix-ui/react-popover';
import { ChevronDown, Plus, X } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { Spinner } from './Spinner';
import type { SelectOption } from './Select';

/**
 * Combobox — type-ahead single select.
 *
 * The patient, procedure and item pickers all run over tables with thousands of
 * rows, so the list is never rendered whole: static options are filtered and
 * capped, and a `loadOptions` search is debounced 250ms and answered by the API.
 * Radix Popover gives the portal and the outside-click handling; the keyboard
 * stays on the text field the whole time, which is what reception types on.
 */

type ComboboxSize = 'sm' | 'md';

/** DS-30 — same padding as `Input`'s shell, so a combobox and a text field of
 *  the same size stand the same height side by side in a filter row. */
const SIZE_INPUT: Record<ComboboxSize, string> = {
  sm: 'p-1 text-[11px]',
  md: 'p-1.5 text-xs',
};

export interface ComboboxProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'value' | 'defaultValue' | 'onChange' | 'size' | 'type' | 'list'
  > {
  value?: string | null;
  /** Fires with the option's value; the clear button fires with `''`. */
  onChange: (value: string, option?: SelectOption) => void;
  /** Static list — filtered locally. Ignored when `loadOptions` is given. */
  options?: SelectOption[];
  /** Server-side search. Debounced 250ms and re-run on every keystroke. */
  loadOptions?: (query: string) => Promise<SelectOption[]>;
  /**
   * The row `value` currently points at. Required with `loadOptions` — the
   * search results do not contain the saved row until the user searches again,
   * so this is the only way the field can show its own label on mount.
   */
  selectedOption?: SelectOption | null;
  placeholder?: string;
  emptyMessage?: string;
  /** Truthy renders the invalid state; the message itself belongs to `FieldError`. */
  error?: boolean | string;
  clearable?: boolean;
  /** Offers "Add «…»" when nothing matches what was typed. */
  allowCreate?: boolean;
  onCreate?: (label: string) => void;
  size?: ComboboxSize;
  /** Rows rendered at once; the rest stay behind a narrower query. */
  maxResults?: number;
  /** Characters required before an async search fires. */
  minQueryLength?: number;
  clearLabel?: string;
  createLabel?: (query: string) => string;
}

type Row = { kind: 'option'; option: SelectOption } | { kind: 'create' };

/** Arabic search has to ignore tashkeel and the alef/ya/ta-marbuta spellings —
 *  "احمد" must find "أحمد" or reception will think the patient is not in the file. */
function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[ً-ْـ]/g, '')
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/[ىئ]/g, 'ي')
    .replace(/ؤ/g, 'و')
    .replace(/ة/g, 'ه')
    .trim();
}

export const Combobox = React.forwardRef<HTMLInputElement, ComboboxProps>(function Combobox(
  {
    value,
    onChange,
    options,
    loadOptions,
    selectedOption,
    placeholder: placeholderProp,
    emptyMessage: emptyMessageProp,
    error,
    disabled,
    clearable = true,
    allowCreate,
    onCreate,
    size = 'md',
    maxResults = 50,
    minQueryLength = 0,
    clearLabel: clearLabelProp,
    createLabel: createLabelProp,
    className,
    onKeyDown,
    onBlur,
    onFocus,
    onMouseDown,
    id,
    ...rest
  },
  ref,
) {
  const { t, i18n } = useTranslation('common');
  const placeholder = placeholderProp ?? t('ui.combobox.placeholder');
  const emptyMessage = emptyMessageProp ?? t('ui.combobox.empty');
  const clearLabel = clearLabelProp ?? t('ui.combobox.clear');
  const createLabel =
    createLabelProp ?? ((value: string) => t('ui.combobox.create', { query: value }));

  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [active, setActive] = React.useState(0);
  const [remote, setRemote] = React.useState<SelectOption[]>([]);
  const [loading, setLoading] = React.useState(false);
  /** Survives the result list being replaced, so the label never blanks out. */
  const [lastPicked, setLastPicked] = React.useState<SelectOption | null>(null);

  const rootRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  const reactId = React.useId();
  const listId = `${reactId}-list`;

  const isAsync = Boolean(loadOptions);
  const pool = React.useMemo(() => (isAsync ? remote : (options ?? [])), [isAsync, remote, options]);

  // A caller that inlines `loadOptions={(q) => api.search(q)}` hands us a new
  // function every render; the ref keeps that out of the effect's deps.
  const loadRef = React.useRef(loadOptions);
  loadRef.current = loadOptions;

  React.useEffect(() => {
    if (!isAsync || !open) return;
    const q = query.trim();
    if (q.length < minQueryLength) {
      setRemote([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    let cancelled = false;
    const timer = window.setTimeout(() => {
      loadRef
        .current!(q)
        .then((result) => {
          if (!cancelled) setRemote(result);
        })
        .catch(() => {
          if (!cancelled) setRemote([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, open, isAsync, minQueryLength]);

  const matched = React.useMemo(() => {
    if (isAsync) return pool; // the server already applied the query
    const q = normalize(query);
    if (!q) return pool;
    return pool.filter(
      (o) => normalize(o.label).includes(q) || (o.description ? normalize(o.description).includes(q) : false),
    );
  }, [isAsync, pool, query]);

  const visible = React.useMemo(() => matched.slice(0, maxResults), [matched, maxResults]);

  const trimmed = query.trim();
  // The empty-query browse list loads silently on open; the spinner is only
  // feedback for something the user typed.
  const searching = loading && trimmed.length > 0;
  const canCreate = Boolean(
    allowCreate &&
      onCreate &&
      trimmed &&
      !visible.some((o) => normalize(o.label) === normalize(trimmed)),
  );

  const rows = React.useMemo<Row[]>(() => {
    const list: Row[] = visible.map((option) => ({ kind: 'option', option }) as Row);
    if (canCreate) list.push({ kind: 'create' });
    return list;
  }, [visible, canCreate]);

  const selected = React.useMemo(() => {
    if (!value) return null;
    return (
      pool.find((o) => o.value === value) ??
      (selectedOption?.value === value ? selectedOption : null) ??
      (lastPicked?.value === value ? lastPicked : null)
    );
  }, [value, pool, selectedOption, lastPicked]);

  const displayLabel = selected?.label ?? (value ?? '');

  /** Only the keyboard may pull the list to the highlight. Turning the wheel
   *  drags rows under a still cursor, which fires `mouseenter` and moves the
   *  highlight — scrolling to it there would yank the list back under the
   *  user's hand on every notch. */
  const followActive = React.useRef(true);

  const highlight = (index: number, fromPointer = false) => {
    followActive.current = !fromPointer;
    setActive(index);
  };

  // An empty query lists the whole pool, so the highlight starts on the row that
  // is already saved. Homing on row 0 there would mean reopening a field and
  // pressing Enter silently swaps the answer for whatever sorts first.
  React.useEffect(() => {
    if (!rows.length) {
      setActive(-1);
      return;
    }
    const current = query
      ? -1
      : rows.findIndex((row) => row.kind === 'option' && row.option.value === value);
    followActive.current = true;
    setActive(current >= 0 ? current : 0);
    // `rows` is read fresh on each run; depending on it would re-home the
    // highlight mid-arrow-key whenever a caller passes an unmemoised `options`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length, query, value]);

  React.useEffect(() => {
    if (!open || !followActive.current) return;
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const close = React.useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);

  const pick = (option: SelectOption) => {
    if (option.disabled) return;
    setLastPicked(option);
    onChange(option.value, option);
    close();
  };

  const commit = (index: number) => {
    const row = rows[index];
    if (!row) return;
    if (row.kind === 'create') {
      onCreate?.(trimmed);
      close();
      return;
    }
    pick(row.option);
  };

  /** Arrow navigation skips disabled rows so Enter can never land on one. */
  const move = (delta: number) => {
    if (!rows.length) return;
    let next = active;
    for (let step = 0; step < rows.length; step += 1) {
      next = (next + delta + rows.length) % rows.length;
      const row = rows[next];
      if (row.kind === 'create' || !row.option.disabled) {
        highlight(next);
        return;
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) setOpen(true);
        else move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (open) move(-1);
        break;
      case 'Home':
        if (open && rows.length) {
          event.preventDefault();
          highlight(0);
        }
        break;
      case 'End':
        if (open && rows.length) {
          event.preventDefault();
          highlight(rows.length - 1);
        }
        break;
      case 'Enter':
        if (open && active >= 0) {
          event.preventDefault();
          commit(active);
        }
        break;
      case 'Escape':
        if (open) {
          event.preventDefault();
          close();
        }
        break;
      case 'Tab':
        if (open) close();
        break;
      default:
        break;
    }
  };

  const showClear = Boolean(clearable && value && !disabled);

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <Popover.Anchor asChild>
        <div ref={rootRef} className="relative">
          <input
            ref={inputRef}
            id={id}
            type="text"
            // Follows the value's own script: a Latin product name in the Arabic
            // UI overflows from its start instead of showing only its tail. Empty,
            // it inherits the page direction so the placeholder stays aligned.
            dir={(open ? query : displayLabel) ? 'auto' : undefined}
            role="combobox"
            autoComplete="off"
            spellCheck={false}
            disabled={disabled}
            aria-expanded={open}
            aria-controls={open ? listId : undefined}
            aria-autocomplete="list"
            aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
            aria-invalid={error ? true : undefined}
            value={open ? query : displayLabel}
            placeholder={open && displayLabel ? displayLabel : placeholder}
            onChange={(event) => {
              setQuery(event.target.value);
              if (!open) setOpen(true);
            }}
            onFocus={(event) => {
              onFocus?.(event);
              if (!disabled) setOpen(true);
            }}
            // Picking a row closes the list but leaves the caret in the field, so
            // a second click fires no focus event. Without this the list only
            // comes back by typing, and the field reads as a dead input.
            onMouseDown={(event) => {
              onMouseDown?.(event);
              if (!event.defaultPrevented && !disabled) setOpen(true);
            }}
            onBlur={(event) => {
              onBlur?.(event);
              const next = event.relatedTarget as Node | null;
              if (next && (rootRef.current?.contains(next) || contentRef.current?.contains(next))) return;
              close();
            }}
            onKeyDown={handleKeyDown}
            className={cn(
              'input-base',
              SIZE_INPUT[size],
              // after the size class: the end padding keeps the text clear of the
              // clear button and the chevron, and must win the merge.
              // Physical side: the input's own `dir` follows its text, but the
              // icons sit at the page's end, so `pe-` would land on the wrong side.
              i18n.dir() === 'rtl' ? 'pl-11' : 'pr-11',
              error && 'border-red-500 focus:ring-red-500',
              className,
            )}
            {...rest}
          />

          <div className="pointer-events-none absolute end-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5">
            {showClear && (
              <button
                type="button"
                aria-label={clearLabel}
                data-testid="combobox-clear"
                tabIndex={-1}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  setLastPicked(null);
                  onChange('');
                  inputRef.current?.focus();
                }}
                className="pointer-events-auto flex h-4 w-4 items-center justify-center rounded text-slate-400 transition hover:bg-red-50 hover:text-red-600"
              >
                <X className="h-3 w-3" />
              </button>
            )}
            {searching ? (
              <Spinner size="sm" className="text-slate-400" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
            )}
          </div>
        </div>
      </Popover.Anchor>

      <Popover.Portal>
        <Popover.Content
          ref={contentRef}
          dir="rtl"
          align="start"
          sideOffset={4}
          // Focus never leaves the text field — the list is driven by the arrows.
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => {
            if (rootRef.current?.contains(event.target as Node)) event.preventDefault();
          }}
          className={cn(
            'z-50 max-h-72 w-[var(--radix-popover-trigger-width)] overflow-hidden',
            'rounded border border-slate-300 bg-white shadow-lg',
            'data-[state=open]:animate-zoomIn',
          )}
        >
          <div
            id={listId}
            ref={listRef}
            role="listbox"
            data-testid="combobox-listbox"
            // Most comboboxes live inside a `Dialog`, and Radix locks scrolling
            // while one is open: `react-remove-scroll` listens for `wheel` on
            // `document` and cancels every event outside the dialog panel. This
            // list is portalled to `body`, so it counts as outside and the wheel
            // dies over it. Stopping the event at the list keeps it away from
            // that listener; `overscroll-contain` then keeps the scroll here
            // instead of chaining to the page once the ends are reached.
            onWheel={(event) => event.stopPropagation()}
            onTouchMove={(event) => event.stopPropagation()}
            className="max-h-72 overflow-y-auto overscroll-contain p-1"
          >
            {searching && rows.length === 0 && (
              <div className="flex items-center justify-center gap-2 px-2 py-3 text-[11px] text-slate-400">
                <Spinner size="sm" />
                {t('ui.combobox.searching')}
              </div>
            )}

            {!loading && rows.length === 0 && (
              <div className="px-2 py-3 text-center text-[11px] text-slate-400">{emptyMessage}</div>
            )}

            {rows.map((row, index) => {
              const isActive = index === active;
              if (row.kind === 'create') {
                return (
                  <div
                    key="__create__"
                    id={`${listId}-${index}`}
                    role="option"
                    data-testid="combobox-create"
                    aria-selected={isActive}
                    data-active={isActive}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => highlight(index, true)}
                    onClick={() => commit(index)}
                    className={cn(
                      'mt-1 flex cursor-pointer items-center gap-1.5 rounded border-t border-slate-100 px-2 py-1 text-xs font-bold text-blue-700',
                      isActive && 'bg-blue-50',
                    )}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    <span className="truncate">{createLabel(trimmed)}</span>
                  </div>
                );
              }

              const option = row.option;
              const Icon = option.icon;
              const isSelected = option.value === value;
              return (
                <div
                  key={option.value}
                  id={`${listId}-${index}`}
                  role="option"
                  data-testid="combobox-option"
                  data-value={option.value}
                  aria-selected={isSelected}
                  aria-disabled={option.disabled}
                  data-active={isActive}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => !option.disabled && highlight(index, true)}
                  onClick={() => pick(option)}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs',
                    isActive && 'bg-blue-50 text-blue-700',
                    isSelected && 'font-bold',
                    option.disabled && 'cursor-not-allowed text-slate-400',
                  )}
                >
                  {option.color && (
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: option.color }}
                    />
                  )}
                  {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-slate-500" />}
                  <span className="min-w-0 break-words" title={option.label}>{option.label}</span>
                  {option.description && (
                    // Capped so a long description (a medication's ingredient
                    // list) truncates instead of squeezing the label to nothing.
                    <span
                      title={option.description}
                      className="ms-auto max-w-[40%] shrink-0 truncate ps-3 text-[10px] font-normal text-slate-400"
                    >
                      {option.description}
                    </span>
                  )}
                </div>
              );
            })}

            {matched.length > maxResults && (
              <div className="border-t border-slate-100 px-2 py-1 text-center text-[10px] text-slate-400">
                {/* DS-51 — the two counts stay LTR and monospaced inside either
                    language's sentence, so the wrapper travels with the text. */}
                <Trans
                  i18nKey="ui.combobox.overflow"
                  ns="common"
                  values={{ shown: maxResults, total: matched.length }}
                  components={[
                    <span className="ltr-num font-mono" key="shown" />,
                    <span className="ltr-num font-mono" key="total" />,
                  ]}
                />
              </div>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
});
